// ══════════════════════════════════════════════════════════════════════════════
// MA BOUTIQUE - PAGE DE CONFIGURATION
// ══════════════════════════════════════════════════════════════════════════════
// Deux modes de création :
// 1. Mode Standard (Gratuit) : Formulaire élégant avec palettes + preview live
// 2. Mode Lia (Premium) : Conversation guidée avec l'IA

import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Store, Settings, Eye, EyeOff, Save, ExternalLink, Sparkles,
  Upload, Palette, Globe, MapPin, Phone, Mail, ArrowRight,
  Facebook, Instagram, Linkedin, Twitter, Youtube, Send,
  CheckCircle, AlertCircle, Copy, RefreshCw, MessageSquare,
  ChevronRight, Zap, Crown, Loader2, Bot, X, Check
} from 'lucide-react';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// ══════════════════════════════════════════════════════════════════════════════
// PALETTES PRÉDÉFINIES
// ══════════════════════════════════════════════════════════════════════════════
const COLOR_PALETTES = [
  { 
    id: 'ocean', 
    name: 'Océan', 
    primary: '#0EA5E9', 
    secondary: '#0369A1',
    accent: '#38BDF8',
    gradient: 'from-sky-500 to-blue-600',
    description: 'Professionnel et serein'
  },
  { 
    id: 'forest', 
    name: 'Forêt', 
    primary: '#10B981', 
    secondary: '#047857',
    accent: '#34D399',
    gradient: 'from-emerald-500 to-green-600',
    description: 'Nature et confiance'
  },
  { 
    id: 'sunset', 
    name: 'Coucher de soleil', 
    primary: '#F97316', 
    secondary: '#C2410C',
    accent: '#FB923C',
    gradient: 'from-orange-500 to-red-500',
    description: 'Dynamique et chaleureux'
  },
  { 
    id: 'lavender', 
    name: 'Lavande', 
    primary: '#8B5CF6', 
    secondary: '#6D28D9',
    accent: '#A78BFA',
    gradient: 'from-violet-500 to-purple-600',
    description: 'Créatif et élégant'
  },
  { 
    id: 'coral', 
    name: 'Corail', 
    primary: '#EC4899', 
    secondary: '#BE185D',
    accent: '#F472B6',
    gradient: 'from-pink-500 to-rose-600',
    description: 'Moderne et audacieux'
  },
  { 
    id: 'slate', 
    name: 'Ardoise', 
    primary: '#475569', 
    secondary: '#1E293B',
    accent: '#64748B',
    gradient: 'from-slate-600 to-slate-800',
    description: 'Sobre et premium'
  },
  { 
    id: 'gold', 
    name: 'Or', 
    primary: '#D97706', 
    secondary: '#92400E',
    accent: '#FBBF24',
    gradient: 'from-amber-500 to-yellow-600',
    description: 'Luxe et prestige'
  },
  { 
    id: 'midnight', 
    name: 'Minuit', 
    primary: '#3B82F6', 
    secondary: '#1E40AF',
    accent: '#60A5FA',
    gradient: 'from-blue-500 to-indigo-600',
    description: 'Classique et fiable'
  },
];

// ══════════════════════════════════════════════════════════════════════════════
// CATÉGORIES MÉTIER — Liste complète harmonisée
// ══════════════════════════════════════════════════════════════════════════════

// Types de boutique
const STORE_TYPES = [
  { id: 'services', label: 'Services / Prestations', icon: '🛠️', description: 'Tu proposes des prestations de service' },
  { id: 'products', label: 'Produits / Vente', icon: '📦', description: 'Tu vends des produits physiques' },
  { id: 'both', label: 'Les deux', icon: '🏪', description: 'Tu proposes des services ET des produits' },
];

const CATEGORIES = [
  // === CATÉGORIE GÉNÉRALE ===
  { value: 'general', label: 'Général', emoji: '🏢', group: 'btp', enabled: true },
  
  // === BTP / ARTISANAT (ACTIF) ===
  { value: 'electricien', label: 'Électricien', emoji: '⚡', group: 'btp', enabled: true },
  { value: 'plombier', label: 'Plombier', emoji: '🔧', group: 'btp', enabled: true },
  { value: 'macon', label: 'Maçon', emoji: '🧱', group: 'btp', enabled: true },
  { value: 'carreleur', label: 'Carreleur', emoji: '🔲', group: 'btp', enabled: true },
  { value: 'peintre', label: 'Peintre', emoji: '🎨', group: 'btp', enabled: true },
  { value: 'menuisier', label: 'Menuisier', emoji: '🪚', group: 'btp', enabled: true },
  { value: 'charpentier', label: 'Charpentier', emoji: '🏗️', group: 'btp', enabled: true },
  { value: 'couvreur', label: 'Couvreur', emoji: '🏠', group: 'btp', enabled: true },
  { value: 'chauffagiste', label: 'Chauffagiste', emoji: '🔥', group: 'btp', enabled: true },
  { value: 'climaticien', label: 'Climaticien', emoji: '❄️', group: 'btp', enabled: true },
  { value: 'pisciniste', label: 'Pisciniste', emoji: '🏊', group: 'btp', enabled: true },
  { value: 'paysagiste', label: 'Paysagiste', emoji: '🌳', group: 'btp', enabled: true },
  { value: 'jardinier', label: 'Jardinier', emoji: '🌱', group: 'btp', enabled: true },
  { value: 'facadier', label: 'Façadier', emoji: '🏛️', group: 'btp', enabled: true },
  { value: 'serrurier', label: 'Serrurier', emoji: '🔐', group: 'btp', enabled: true },
  { value: 'vitrier', label: 'Vitrier', emoji: '🪟', group: 'btp', enabled: true },
  
  // === BEAUTÉ / BIEN-ÊTRE (BIENTÔT) ===
  { value: 'coiffeur', label: 'Coiffeur', emoji: '💇', group: 'beaute', enabled: false },
  { value: 'estheticienne', label: 'Esthéticienne', emoji: '💅', group: 'beaute', enabled: false },
  { value: 'barbier', label: 'Barbier', emoji: '🧔', group: 'beaute', enabled: false },
  { value: 'spa', label: 'Spa / Massage', emoji: '💆', group: 'beaute', enabled: false },
  { value: 'maquilleur', label: 'Maquilleur', emoji: '💄', group: 'beaute', enabled: false },
  
  // === SPORT / COACHING (BIENTÔT) ===
  { value: 'coach_sportif', label: 'Coach sportif', emoji: '💪', group: 'sport', enabled: false },
  { value: 'personal_trainer', label: 'Personal Trainer', emoji: '🏋️', group: 'sport', enabled: false },
  { value: 'yoga', label: 'Yoga / Pilates', emoji: '🧘', group: 'sport', enabled: false },
  { value: 'nutritionniste', label: 'Nutritionniste', emoji: '🥗', group: 'sport', enabled: false },
  
  // === COMMERCE / VENTE (BIENTÔT) ===
  { value: 'mode', label: 'Mode / Vêtements', emoji: '👗', group: 'commerce', enabled: false },
  { value: 'bijoux', label: 'Bijoux / Accessoires', emoji: '💎', group: 'commerce', enabled: false },
  { value: 'alimentation', label: 'Alimentation', emoji: '🍽️', group: 'commerce', enabled: false },
  { value: 'epicerie', label: 'Épicerie / Primeur', emoji: '🥬', group: 'commerce', enabled: false },
  { value: 'boulangerie', label: 'Boulangerie / Pâtisserie', emoji: '🥐', group: 'commerce', enabled: false },
  { value: 'fleuriste', label: 'Fleuriste', emoji: '💐', group: 'commerce', enabled: false },
  { value: 'deco', label: 'Décoration', emoji: '🏡', group: 'commerce', enabled: false },
  
  // === SERVICES (BIENTÔT) ===
  { value: 'informatique', label: 'Informatique', emoji: '💻', group: 'services', enabled: false },
  { value: 'photographe', label: 'Photographe', emoji: '📷', group: 'services', enabled: false },
  { value: 'graphiste', label: 'Graphiste', emoji: '🎨', group: 'services', enabled: false },
  { value: 'traiteur', label: 'Traiteur', emoji: '🍴', group: 'services', enabled: false },
  { value: 'nettoyage', label: 'Nettoyage', emoji: '🧹', group: 'services', enabled: false },
  { value: 'demenagement', label: 'Déménagement', emoji: '📦', group: 'services', enabled: false },
  { value: 'garde_enfants', label: 'Garde d\'enfants', emoji: '👶', group: 'services', enabled: false },
  { value: 'aide_domicile', label: 'Aide à domicile', emoji: '🏠', group: 'services', enabled: false },
  { value: 'cours_particuliers', label: 'Cours particuliers', emoji: '📚', group: 'services', enabled: false },
  
  // === AUTO / MOTO (BIENTÔT) ===
  { value: 'garage', label: 'Garage / Mécanicien', emoji: '🚗', group: 'auto', enabled: false },
  { value: 'carrossier', label: 'Carrossier', emoji: '🔧', group: 'auto', enabled: false },
  { value: 'lavage_auto', label: 'Lavage auto', emoji: '🧽', group: 'auto', enabled: false },
  
  // === AUTRE (BIENTÔT) ===
  { value: 'autre', label: 'Autre', emoji: '✨', group: 'autre', enabled: false },
];

// Groupes de catégories pour l'affichage
const CATEGORY_GROUPS = [
  { id: 'btp', label: 'BTP / Artisanat', emoji: '🏗️', enabled: true },
  { id: 'beaute', label: 'Beauté / Bien-être', emoji: '💅', enabled: false },
  { id: 'sport', label: 'Sport / Coaching', emoji: '💪', enabled: false },
  { id: 'commerce', label: 'Commerce / Vente', emoji: '🛍️', enabled: false },
  { id: 'services', label: 'Services', emoji: '🛠️', enabled: false },
  { id: 'auto', label: 'Auto / Moto', emoji: '🚗', enabled: false },
  { id: 'autre', label: 'Autre', emoji: '✨', enabled: false },
];

// ══════════════════════════════════════════════════════════════════════════════
// COMPOSANT PRINCIPAL
// ══════════════════════════════════════════════════════════════════════════════

export default function MyStorePage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [store, setStore] = useState(null);
  const [hasStore, setHasStore] = useState(false);
  const [activeTab, setActiveTab] = useState('general');
  const [showLiaMode, setShowLiaMode] = useState(false);
  
  // Formulaire de création standard
  const [createForm, setCreateForm] = useState({
    name: '',
    description: '',
    tagline: '',
    category: 'general',
    storeType: 'services', // services | products | both
    selectedPalette: 'midnight'
  });
  
  // Étape du formulaire (pour animation)
  const [createStep, setCreateStep] = useState(1);

  // Charger la boutique existante
  useEffect(() => {
    fetchStore();
  }, []);

  const fetchStore = async () => {
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('client_token');
      const response = await fetch(`${API_URL}/api/stores/my-store`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const data = await response.json();
      
      if (data.exists) {
        setStore(data.store);
        setHasStore(true);
      } else {
        setHasStore(false);
      }
    } catch (err) {
      console.error('Erreur chargement boutique:', err);
    } finally {
      setLoading(false);
    }
  };

  // Créer une boutique (mode standard)
  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);

    const palette = COLOR_PALETTES.find(p => p.id === createForm.selectedPalette) || COLOR_PALETTES[7];

    try {
      const token = localStorage.getItem('token') || localStorage.getItem('client_token');
      const response = await fetch(`${API_URL}/api/stores`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          name: createForm.name,
          description: createForm.description,
          tagline: createForm.tagline,
          category: createForm.category,
          store_type: createForm.storeType, // services | products | both
          theme: {
            primary_color: palette.primary,
            secondary_color: palette.secondary,
            accent_color: palette.accent,
            style: 'modern'
          }
        })
      });

      const data = await response.json();

      if (data.success) {
        toast.success('Boutique créée avec succès !');
        setStore(data.store);
        setHasStore(true);
      } else {
        toast.error(data.detail || 'Erreur lors de la création');
      }
    } catch (err) {
      console.error('Erreur création:', err);
      toast.error('Erreur de connexion');
    } finally {
      setSaving(false);
    }
  };

  // Mettre à jour la boutique
  const handleUpdate = async (updates) => {
    setSaving(true);

    try {
      const token = localStorage.getItem('token') || localStorage.getItem('client_token');
      const response = await fetch(`${API_URL}/api/stores/my-store`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(updates)
      });

      const data = await response.json();

      if (data.success) {
        toast.success('Modifications enregistrées');
        setStore(data.store);
      } else {
        toast.error(data.detail || 'Erreur lors de la sauvegarde');
      }
    } catch (err) {
      console.error('Erreur mise à jour:', err);
      toast.error('Erreur de connexion');
    } finally {
      setSaving(false);
    }
  };

  // Publier / Dépublier
  const togglePublish = async () => {
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('client_token');
      const endpoint = store.is_published ? 'unpublish' : 'publish';
      
      const response = await fetch(`${API_URL}/api/stores/my-store/${endpoint}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const data = await response.json();

      if (data.success) {
        setStore({ ...store, is_published: !store.is_published });
        toast.success(store.is_published ? 'Boutique dépubliée' : 'Boutique publiée !');
      }
    } catch (err) {
      console.error('Erreur publication:', err);
    }
  };

  // Copier l'URL
  const copyUrl = (url) => {
    navigator.clipboard.writeText(url);
    toast.success('URL copiée !');
  };

  // Obtenir la palette sélectionnée
  const getSelectedPalette = () => {
    return COLOR_PALETTES.find(p => p.id === createForm.selectedPalette) || COLOR_PALETTES[7];
  };

  // ══════════════════════════════════════════════════════════════════════════════
  // RENDU - CHARGEMENT
  // ══════════════════════════════════════════════════════════════════════════════
  
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <RefreshCw className="animate-spin text-blue-500" size={32} />
      </div>
    );
  }

  // ══════════════════════════════════════════════════════════════════════════════
  // RENDU - CRÉATION (Pas de boutique)
  // ══════════════════════════════════════════════════════════════════════════════
  
  if (!hasStore) {
    const selectedPalette = getSelectedPalette();
    
    return (
      <div className="min-h-[80vh] bg-gradient-to-br from-slate-50 to-blue-50 py-8">
        <div className="max-w-5xl mx-auto px-4">
          
          {/* Header */}
          <motion.div 
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-8"
          >
            <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
              <Store className="text-white" size={40} />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Crée ta vitrine</h1>
            <p className="text-gray-600 max-w-md mx-auto">
              Ta boutique en ligne pour présenter tes produits et services. 
              Accessible sur le marketplace et avec ton propre lien.
            </p>
          </motion.div>

          {/* Choix du mode */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="grid md:grid-cols-2 gap-6 mb-8"
          >
            {/* Mode Standard */}
            <button
              onClick={() => setShowLiaMode(false)}
              className={`relative p-6 rounded-2xl text-left transition-all ${
                !showLiaMode 
                  ? 'bg-white ring-2 ring-blue-500 shadow-lg' 
                  : 'bg-white/80 hover:bg-white hover:shadow-md'
              }`}
              data-testid="mode-standard-btn"
            >
              {!showLiaMode && (
                <div className="absolute -top-2 -right-2 bg-blue-500 text-white text-xs px-2 py-1 rounded-full">
                  Sélectionné
                </div>
              )}
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Palette className="text-blue-600" size={24} />
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-bold text-gray-900">Mode Standard</h3>
                    <span className="text-xs px-2 py-0.5 bg-green-100 text-green-700 rounded-full">Gratuit</span>
                  </div>
                  <p className="text-sm text-gray-600">
                    Formulaire simple avec palettes prédéfinies. Création en 2 minutes.
                  </p>
                </div>
              </div>
            </button>

            {/* Mode Lia (Premium) */}
            <button
              onClick={() => setShowLiaMode(true)}
              className={`relative p-6 rounded-2xl text-left transition-all ${
                showLiaMode 
                  ? 'bg-gradient-to-br from-[#1E40AF] to-[#3B82F6] ring-2 ring-[#1E40AF] shadow-lg text-white' 
                  : 'bg-white/80 hover:bg-white hover:shadow-md'
              }`}
              data-testid="mode-lia-btn"
            >
              {showLiaMode && (
                <div className="absolute -top-2 -right-2 bg-[#F97316] text-white text-xs px-2 py-1 rounded-full font-medium">
                  Sélectionné
                </div>
              )}
              <div className="flex items-start gap-4">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${
                  showLiaMode ? 'bg-white/20' : 'bg-blue-100'
                }`}>
                  <Sparkles className={showLiaMode ? 'text-white' : 'text-[#1E40AF]'} size={24} />
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className={`font-bold ${showLiaMode ? 'text-white' : 'text-gray-900'}`}>Créer avec ton assistant</h3>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                      showLiaMode ? 'bg-white/20 text-white' : 'bg-orange-100 text-orange-700'
                    }`}>
                      Premium
                    </span>
                  </div>
                  <p className={`text-sm ${showLiaMode ? 'text-white/90' : 'text-gray-600'}`}>
                    Ton assistant te pose des questions et crée une boutique personnalisée.
                  </p>
                </div>
              </div>
            </button>
          </motion.div>

          {/* Contenu selon le mode */}
          <AnimatePresence mode="wait">
            {showLiaMode ? (
              <LiaCreationMode 
                key="lia" 
                onStoreCreated={(newStore) => {
                  setStore(newStore);
                  setHasStore(true);
                }}
              />
            ) : (
              <StandardCreationMode
                key="standard"
                createForm={createForm}
                setCreateForm={setCreateForm}
                createStep={createStep}
                setCreateStep={setCreateStep}
                selectedPalette={selectedPalette}
                saving={saving}
                onSubmit={handleCreate}
              />
            )}
          </AnimatePresence>
        </div>
      </div>
    );
  }

  // ══════════════════════════════════════════════════════════════════════════════
  // RENDU - BOUTIQUE EXISTANTE (Configuration)
  // ══════════════════════════════════════════════════════════════════════════════
  
  return (
    <div className="max-w-5xl mx-auto px-4 py-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Ma Boutique</h1>
          <p className="text-gray-500 text-sm mt-1">Configure ta vitrine en ligne</p>
        </div>
        
        <div className="flex items-center gap-3">
          <button
            onClick={togglePublish}
            data-testid="toggle-publish-btn"
            className={`px-4 py-2 rounded-lg font-medium flex items-center gap-2 transition ${
              store.is_published
                ? 'bg-green-100 text-green-700 hover:bg-green-200'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {store.is_published ? (
              <>
                <Eye size={18} />
                Publiée
              </>
            ) : (
              <>
                <EyeOff size={18} />
                Non publiée
              </>
            )}
          </button>
          
          <Link
            to={`/pro/${store.slug}`}
            target="_blank"
            data-testid="view-store-btn"
            className="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium flex items-center gap-2 hover:bg-blue-700 transition"
          >
            <ExternalLink size={18} />
            Voir
          </Link>
        </div>
      </div>

      {/* URLs */}
      <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-xl p-4 mb-6">
        <h3 className="text-sm font-semibold text-blue-900 mb-3">Tes liens</h3>
        <div className="space-y-2">
          <div className="flex items-center gap-2 bg-white rounded-lg px-3 py-2">
            <Globe size={16} className="text-blue-500" />
            <span className="text-sm text-gray-600 flex-1 truncate">
              {window.location.origin}/pro/{store.slug}
            </span>
            <button
              onClick={() => copyUrl(`${window.location.origin}/pro/${store.slug}`)}
              className="p-1.5 hover:bg-gray-100 rounded-lg transition"
              data-testid="copy-url-btn"
            >
              <Copy size={14} className="text-gray-500" />
            </button>
          </div>
          <p className="text-xs text-blue-700 ml-6">
            Subdomain à venir : {store.slug}.abracadaworld.com
          </p>
        </div>
      </div>

      {/* Onglets */}
      <div className="flex gap-1 border-b border-gray-200 mb-6 overflow-x-auto">
        {[
          { id: 'general', label: 'Général', icon: Settings },
          { id: 'theme', label: 'Apparence', icon: Palette },
          { id: 'contact', label: 'Contact', icon: MapPin },
          { id: 'social', label: 'Réseaux', icon: Globe },
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            data-testid={`tab-${tab.id}`}
            className={`px-4 py-3 text-sm font-medium flex items-center gap-2 border-b-2 transition whitespace-nowrap ${
              activeTab === tab.id
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-600 hover:text-gray-900'
            }`}
          >
            <tab.icon size={16} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Contenu des onglets */}
      <div className="bg-white rounded-xl p-6 shadow-sm">
        {activeTab === 'general' && (
          <GeneralTab store={store} onUpdate={handleUpdate} saving={saving} />
        )}
        {activeTab === 'theme' && (
          <ThemeTab store={store} onUpdate={handleUpdate} saving={saving} />
        )}
        {activeTab === 'contact' && (
          <ContactTab store={store} onUpdate={handleUpdate} saving={saving} />
        )}
        {activeTab === 'social' && (
          <SocialTab store={store} onUpdate={handleUpdate} saving={saving} />
        )}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// MODE CRÉATION STANDARD (Formulaire avec palettes)
// ══════════════════════════════════════════════════════════════════════════════

function StandardCreationMode({ createForm, setCreateForm, createStep, setCreateStep, selectedPalette, saving, onSubmit }) {
  
  const [showAllCategories, setShowAllCategories] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState('btp'); // BTP par défaut
  
  const isStep1Valid = createForm.name.trim().length >= 2;
  
  // Filtrer les catégories par groupe (BTP uniquement pour le moment)
  const getFilteredCategories = () => {
    // Toujours afficher les catégories BTP
    return CATEGORIES.filter(c => c.group === 'btp');
  };
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="grid lg:grid-cols-5 gap-8"
    >
      {/* Formulaire (3 colonnes) */}
      <div className="lg:col-span-3">
        <form onSubmit={onSubmit} className="bg-white rounded-2xl p-6 shadow-sm space-y-6">
          
          {/* Nom de la boutique */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Nom de ta boutique *
            </label>
            <input
              type="text"
              required
              value={createForm.name}
              onChange={(e) => setCreateForm({...createForm, name: e.target.value})}
              placeholder="Ex: Dupont Électricité, Salon Marie..."
              className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-lg"
              data-testid="store-name-input"
            />
          </div>

          {/* Slogan */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Slogan / Tagline
            </label>
            <input
              type="text"
              value={createForm.tagline}
              onChange={(e) => setCreateForm({...createForm, tagline: e.target.value})}
              placeholder="Ex: Ton électricien de confiance depuis 2010"
              className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              data-testid="store-tagline-input"
            />
          </div>

          {/* Type de boutique */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-3">
              Que proposes-tu ?
            </label>
            <div className="grid grid-cols-3 gap-3">
              {STORE_TYPES.map(type => (
                <button
                  key={type.id}
                  type="button"
                  onClick={() => setCreateForm({...createForm, storeType: type.id})}
                  className={`p-4 rounded-xl text-center transition-all border-2 ${
                    createForm.storeType === type.id
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  data-testid={`store-type-${type.id}`}
                >
                  <span className="text-2xl block mb-2">{type.icon}</span>
                  <span className="text-sm font-medium text-gray-800 block">{type.label}</span>
                  <span className="text-xs text-gray-500 mt-1 block">{type.description}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Catégorie avec groupes */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-3">
              Ton secteur d'activité
            </label>
            
            {/* Groupes de catégories */}
            <div className="flex flex-wrap gap-2 mb-3">
              {CATEGORY_GROUPS.map(group => (
                <button
                  key={group.id}
                  type="button"
                  onClick={() => group.enabled && setSelectedGroup(group.id === selectedGroup ? null : group.id)}
                  disabled={!group.enabled}
                  className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all relative ${
                    !group.enabled
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : selectedGroup === group.id 
                        ? 'bg-blue-100 text-blue-700' 
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {group.emoji} {group.label}
                  {!group.enabled && (
                    <span className="absolute -top-1 -right-1 text-[10px] bg-gray-300 text-gray-600 px-1.5 py-0.5 rounded-full">
                      Bientôt
                    </span>
                  )}
                </button>
              ))}
            </div>
            
            {/* Grille de catégories */}
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
              {getFilteredCategories().map(cat => (
                <button
                  key={cat.value}
                  type="button"
                  onClick={() => cat.enabled && setCreateForm({...createForm, category: cat.value})}
                  disabled={!cat.enabled}
                  className={`p-2.5 rounded-xl text-center transition-all ${
                    !cat.enabled
                      ? 'bg-gray-50 text-gray-300 cursor-not-allowed opacity-60'
                      : createForm.category === cat.value
                        ? 'bg-blue-100 ring-2 ring-blue-500 text-blue-700'
                        : 'bg-gray-50 hover:bg-gray-100 text-gray-700'
                  }`}
                  data-testid={`category-${cat.value}`}
                >
                  <span className={`text-xl block mb-1 ${!cat.enabled ? 'grayscale' : ''}`}>{cat.emoji}</span>
                  <span className="text-xs font-medium">{cat.label}</span>
                </button>
              ))}
            </div>
            
            {/* Select pour toutes les catégories BTP */}
            <select
              value={createForm.category}
              onChange={(e) => setCreateForm({...createForm, category: e.target.value})}
              className="mt-3 w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm"
            >
              <option value="">-- Choisir une catégorie --</option>
              <optgroup label="🏗️ BTP / Artisanat">
                {CATEGORIES.filter(c => c.group === 'btp' && c.enabled).map(cat => (
                  <option key={cat.value} value={cat.value}>{cat.emoji} {cat.label}</option>
                ))}
              </optgroup>
              <optgroup label="🔒 Bientôt disponible" disabled>
                <option disabled>💅 Beauté / Bien-être</option>
                <option disabled>💪 Sport / Coaching</option>
                <option disabled>🛍️ Commerce / Vente</option>
                <option disabled>🚗 Auto / Moto</option>
              </optgroup>
            </select>
          </div>

          {/* Palette de couleurs */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-3">
              Choisis ton style
            </label>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {COLOR_PALETTES.map(palette => (
                <button
                  key={palette.id}
                  type="button"
                  onClick={() => setCreateForm({...createForm, selectedPalette: palette.id})}
                  className={`relative p-3 rounded-xl transition-all ${
                    createForm.selectedPalette === palette.id
                      ? 'ring-2 ring-offset-2 ring-gray-900 scale-105'
                      : 'hover:scale-102'
                  }`}
                  data-testid={`palette-${palette.id}`}
                >
                  <div 
                    className={`h-12 rounded-lg bg-gradient-to-r ${palette.gradient} mb-2`}
                  />
                  <span className="text-xs font-medium text-gray-700 block">{palette.name}</span>
                  {createForm.selectedPalette === palette.id && (
                    <div className="absolute -top-1 -right-1 w-5 h-5 bg-gray-900 rounded-full flex items-center justify-center">
                      <Check size={12} className="text-white" />
                    </div>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Description <span className="text-gray-400 font-normal">(optionnel)</span>
            </label>
            <textarea
              rows={3}
              value={createForm.description}
              onChange={(e) => setCreateForm({...createForm, description: e.target.value})}
              placeholder="Décris ton activité, tes spécialités..."
              className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
              data-testid="store-description-input"
            />
          </div>

          {/* Bouton de création */}
          <button
            type="submit"
            disabled={saving || !isStep1Valid}
            className="w-full py-4 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl font-semibold hover:from-blue-700 hover:to-blue-800 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3 text-lg shadow-lg"
            data-testid="create-store-btn"
          >
            {saving ? (
              <Loader2 className="animate-spin" size={22} />
            ) : (
              <Store size={22} />
            )}
            Créer ma boutique
          </button>
        </form>
      </div>

      {/* Preview Live (2 colonnes) */}
      <div className="lg:col-span-2">
        <div className="sticky top-24">
          <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">
            Aperçu en direct
          </h3>
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-100">
            {/* Header preview */}
            <div 
              className={`h-24 bg-gradient-to-r ${selectedPalette.gradient}`}
            />
            <div className="px-4 -mt-8">
              <div 
                className="w-16 h-16 rounded-xl bg-white shadow-md border-4 flex items-center justify-center"
                style={{ borderColor: selectedPalette.primary }}
              >
                <span 
                  className="text-2xl font-bold"
                  style={{ color: selectedPalette.primary }}
                >
                  {createForm.name?.charAt(0)?.toUpperCase() || '?'}
                </span>
              </div>
            </div>
            <div className="p-4 pt-3">
              <h4 className="font-bold text-gray-900 text-lg">
                {createForm.name || 'Nom de ta boutique'}
              </h4>
              {createForm.tagline && (
                <p className="text-sm text-gray-500 mt-0.5">{createForm.tagline}</p>
              )}
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <span className="px-2 py-1 bg-gray-100 rounded-full text-xs text-gray-600">
                  {CATEGORIES.find(c => c.value === createForm.category)?.emoji}{' '}
                  {CATEGORIES.find(c => c.value === createForm.category)?.label}
                </span>
                <span className="px-2 py-1 bg-blue-50 text-blue-600 rounded-full text-xs">
                  {STORE_TYPES.find(t => t.id === createForm.storeType)?.icon}{' '}
                  {createForm.storeType === 'services' ? 'Services' : 
                   createForm.storeType === 'products' ? 'Produits' : 'Services & Produits'}
                </span>
              </div>
              {createForm.description && (
                <p className="mt-3 text-sm text-gray-600 line-clamp-3">
                  {createForm.description}
                </p>
              )}
              <button
                className="mt-4 w-full py-2.5 rounded-lg font-medium text-white text-sm"
                style={{ backgroundColor: selectedPalette.primary }}
              >
                Contacter
              </button>
            </div>
          </div>
          
          {/* Info palette */}
          <div className="mt-4 p-3 bg-gray-50 rounded-xl">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-4 h-4 rounded-full" style={{ backgroundColor: selectedPalette.primary }} />
              <span className="text-sm font-medium text-gray-700">{selectedPalette.name}</span>
            </div>
            <p className="text-xs text-gray-500">{selectedPalette.description}</p>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// MODE CRÉATION AVEC ABRA (Conversation IA)
// ══════════════════════════════════════════════════════════════════════════════

function LiaCreationMode({ onStoreCreated }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [step, setStep] = useState(0);
  const [storeData, setStoreData] = useState({});
  const [isCreating, setIsCreating] = useState(false);
  const messagesEndRef = useRef(null);

  // Questions de l'assistant
  const liaQuestions = [
    {
      id: 'welcome',
      message: "Salut ! Je suis ton super assistant. Tu peux m'appeler comme tu veux ! Je vais t'aider à créer une boutique qui te ressemble. Commençons : **Comment s'appelle ton entreprise ?**"
    },
    {
      id: 'activity',
      message: "Super ! Et **quel est ton secteur d'activité** ? (ex: électricité, coiffure, coaching, mode...)"
    },
    {
      id: 'style',
      message: "Excellent choix ! Maintenant, dis-moi : **Quel style te correspond le mieux ?**\n\n• **Moderne** - Épuré et contemporain\n• **Chaleureux** - Accueillant et convivial\n• **Premium** - Luxe et raffinement\n• **Naturel** - Authentique et écologique"
    },
    {
      id: 'description',
      message: "Parfait ! Dernière question : **Décris en quelques mots ce qui te rend unique** (tes spécialités, ton approche, ce que tes clients apprécient...)"
    },
    {
      id: 'confirm',
      message: "J'ai tout ce qu'il me faut ! Voici ce que je te propose..."
    }
  ];

  // Style mapping
  const styleToColors = {
    'moderne': { palette: 'midnight', style: 'modern' },
    'épuré': { palette: 'midnight', style: 'modern' },
    'chaleureux': { palette: 'sunset', style: 'warm' },
    'accueillant': { palette: 'sunset', style: 'warm' },
    'convivial': { palette: 'forest', style: 'warm' },
    'premium': { palette: 'gold', style: 'luxury' },
    'luxe': { palette: 'gold', style: 'luxury' },
    'raffiné': { palette: 'slate', style: 'luxury' },
    'naturel': { palette: 'forest', style: 'natural' },
    'authentique': { palette: 'forest', style: 'natural' },
    'écologique': { palette: 'forest', style: 'natural' },
    'créatif': { palette: 'lavender', style: 'creative' },
    'audacieux': { palette: 'coral', style: 'creative' },
  };

  // Initialiser la conversation
  useEffect(() => {
    const timer = setTimeout(() => {
      setMessages([{ role: 'assistant', content: liaQuestions[0].message }]);
    }, 500);
    return () => clearTimeout(timer);
  }, []);

  // Scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Envoyer un message
  const handleSend = async () => {
    if (!input.trim()) return;
    
    const userMessage = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setIsTyping(true);

    // Traiter la réponse selon l'étape
    await new Promise(resolve => setTimeout(resolve, 800));
    
    const newStep = step + 1;
    let newStoreData = { ...storeData };

    // Sauvegarder la donnée selon l'étape
    if (step === 0) {
      newStoreData.name = userMessage;
    } else if (step === 1) {
      newStoreData.category = userMessage;
    } else if (step === 2) {
      // Détecter le style
      const lowerInput = userMessage.toLowerCase();
      const matchedStyle = Object.keys(styleToColors).find(key => lowerInput.includes(key));
      newStoreData.styleChoice = matchedStyle ? styleToColors[matchedStyle] : styleToColors['moderne'];
    } else if (step === 3) {
      newStoreData.description = userMessage;
    }

    setStoreData(newStoreData);
    setStep(newStep);

    // Répondre
    if (newStep < liaQuestions.length - 1) {
      setMessages(prev => [...prev, { role: 'assistant', content: liaQuestions[newStep].message }]);
    } else {
      // Fin - Proposer le résultat
      const palette = COLOR_PALETTES.find(p => p.id === (newStoreData.styleChoice?.palette || 'midnight'));
      const tagline = generateTagline(newStoreData.category);
      
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: `J'ai créé ta boutique personnalisée !\n\n**${newStoreData.name}**\n_${tagline}_\n\nPalette : ${palette?.name || 'Minuit'}\nCatégorie : ${newStoreData.category}\n\nClique sur le bouton ci-dessous pour confirmer !`,
        showConfirm: true,
        storeData: {
          ...newStoreData,
          tagline,
          palette
        }
      }]);
    }

    setIsTyping(false);
  };

  // Générer un tagline automatique
  const generateTagline = (category) => {
    const taglines = {
      'électricité': 'Ton électricien de confiance',
      'plomberie': 'Solutions plomberie rapides et efficaces',
      'coiffure': 'Révèle ta beauté naturelle',
      'esthétique': 'Prends soin de toi',
      'coaching': 'Atteins tes objectifs',
      'mode': 'Style et élégance au quotidien',
      default: 'L\'excellence à ton service'
    };
    const lower = category?.toLowerCase() || '';
    for (const [key, value] of Object.entries(taglines)) {
      if (lower.includes(key)) return value;
    }
    return taglines.default;
  };

  // Créer la boutique
  const createStore = async (data) => {
    setIsCreating(true);
    
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('client_token');
      const response = await fetch(`${API_URL}/api/stores`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          name: data.name,
          description: data.description || '',
          tagline: data.tagline,
          category: detectCategory(data.category),
          theme: {
            primary_color: data.palette?.primary || '#3B82F6',
            secondary_color: data.palette?.secondary || '#1E40AF',
            accent_color: data.palette?.accent || '#60A5FA',
            style: data.styleChoice?.style || 'modern'
          }
        })
      });

      const result = await response.json();

      if (result.success) {
        toast.success('Boutique créée avec Lia !');
        onStoreCreated(result.store);
      } else {
        toast.error(result.detail || 'Erreur lors de la création');
      }
    } catch (err) {
      console.error('Erreur création:', err);
      toast.error('Erreur de connexion');
    } finally {
      setIsCreating(false);
    }
  };

  // Détecter la catégorie depuis le texte libre
  const detectCategory = (text) => {
    const lower = text?.toLowerCase() || '';
    for (const cat of CATEGORIES) {
      if (lower.includes(cat.label.toLowerCase())) {
        return cat.value;
      }
    }
    return 'general';
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="max-w-2xl mx-auto"
    >
      <div className="bg-gradient-to-br from-[#1E40AF] to-[#3B82F6] rounded-2xl shadow-xl overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 flex items-center gap-3 border-b border-white/10">
          <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
            <Sparkles className="text-white" size={20} />
          </div>
          <div>
            <h3 className="font-semibold text-white">Ton Assistant</h3>
            <p className="text-xs text-white/70">Ton assistant création</p>
          </div>
        </div>

        {/* Messages */}
        <div className="h-[400px] overflow-y-auto p-4 space-y-4 bg-white/5">
          {messages.map((msg, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div className={`max-w-[85%] p-4 rounded-2xl ${
                msg.role === 'user'
                  ? 'bg-white text-gray-800'
                  : 'bg-white/10 text-white'
              }`}>
                <p className="whitespace-pre-line text-sm leading-relaxed">
                  {msg.content.split('**').map((part, idx) => 
                    idx % 2 === 1 ? <strong key={idx}>{part}</strong> : part
                  )}
                </p>
                {msg.showConfirm && (
                  <button
                    onClick={() => createStore(msg.storeData)}
                    disabled={isCreating}
                    className="mt-4 w-full py-3 bg-[#F97316] text-white rounded-xl font-semibold hover:bg-[#E8650A] transition flex items-center justify-center gap-2"
                    data-testid="confirm-lia-store-btn"
                  >
                    {isCreating ? (
                      <Loader2 className="animate-spin" size={18} />
                    ) : (
                      <CheckCircle size={18} />
                    )}
                    {isCreating ? 'Création...' : 'Confirmer et créer'}
                  </button>
                )}
              </div>
            </motion.div>
          ))}
          
          {isTyping && (
            <div className="flex justify-start">
              <div className="bg-white/10 text-white p-4 rounded-2xl">
                <div className="flex gap-1.5">
                  <span className="w-2 h-2 bg-white/60 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                  <span className="w-2 h-2 bg-white/60 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                  <span className="w-2 h-2 bg-white/60 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                </div>
              </div>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <div className="p-4 bg-white/10 border-t border-white/10">
          <div className="flex gap-2">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSend()}
              placeholder="Ta réponse..."
              className="flex-1 px-4 py-3 bg-white/20 text-white placeholder-white/50 rounded-xl focus:outline-none focus:ring-2 focus:ring-white/30"
              data-testid="lia-input"
            />
            <button
              onClick={handleSend}
              disabled={!input.trim() || isTyping}
              className="px-4 py-3 bg-[#F97316] text-white rounded-xl hover:bg-[#E8650A] transition disabled:opacity-50"
              data-testid="lia-send-btn"
            >
              <Send size={20} />
            </button>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// SOUS-COMPOSANTS ONGLETS (Configuration boutique existante)
// ══════════════════════════════════════════════════════════════════════════════

function GeneralTab({ store, onUpdate, saving }) {
  const [form, setForm] = useState({
    name: store.name || '',
    tagline: store.tagline || '',
    description: store.description || '',
    category: store.category || 'general'
  });

  const handleSave = () => {
    onUpdate(form);
  };

  return (
    <div className="space-y-5">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Nom de la boutique</label>
        <input
          type="text"
          value={form.name}
          onChange={(e) => setForm({...form, name: e.target.value})}
          className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          data-testid="edit-name-input"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Slogan</label>
        <input
          type="text"
          value={form.tagline}
          onChange={(e) => setForm({...form, tagline: e.target.value})}
          placeholder="Ex: Votre partenaire de confiance"
          className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          data-testid="edit-tagline-input"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Catégorie</label>
        <select
          value={form.category}
          onChange={(e) => setForm({...form, category: e.target.value})}
          className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          data-testid="edit-category-select"
        >
          {CATEGORIES.map(cat => (
            <option key={cat.value} value={cat.value}>{cat.emoji} {cat.label}</option>
          ))}
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
        <textarea
          rows={5}
          value={form.description}
          onChange={(e) => setForm({...form, description: e.target.value})}
          placeholder="Décris ton activité..."
          className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
          data-testid="edit-description-input"
        />
      </div>

      <button
        onClick={handleSave}
        disabled={saving}
        className="px-6 py-2.5 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition disabled:opacity-50 flex items-center gap-2"
        data-testid="save-general-btn"
      >
        {saving ? <RefreshCw className="animate-spin" size={16} /> : <Save size={16} />}
        Enregistrer
      </button>
    </div>
  );
}

function ThemeTab({ store, onUpdate, saving }) {
  const theme = store.theme || {};
  const [form, setForm] = useState({
    primary_color: theme.primary_color || '#3B82F6',
    secondary_color: theme.secondary_color || '#1E40AF',
    accent_color: theme.accent_color || '#F59E0B',
    style: theme.style || 'modern'
  });

  const handleSave = () => {
    onUpdate({ theme: form });
  };

  return (
    <div className="space-y-6">
      {/* Couleurs prédéfinies */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-3">Thème de couleur</label>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {COLOR_PALETTES.map((palette) => (
            <button
              key={palette.id}
              type="button"
              onClick={() => setForm({
                ...form,
                primary_color: palette.primary,
                secondary_color: palette.secondary,
                accent_color: palette.accent
              })}
              className={`relative p-3 rounded-xl transition-all ${
                form.primary_color === palette.primary
                  ? 'ring-2 ring-offset-2 ring-gray-900 scale-105'
                  : 'hover:scale-102'
              }`}
              data-testid={`theme-palette-${palette.id}`}
            >
              <div className={`h-12 rounded-lg bg-gradient-to-r ${palette.gradient} mb-2`} />
              <span className="text-xs font-medium text-gray-700 block">{palette.name}</span>
              {form.primary_color === palette.primary && (
                <div className="absolute -top-1 -right-1 w-5 h-5 bg-gray-900 rounded-full flex items-center justify-center">
                  <Check size={12} className="text-white" />
                </div>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Couleurs personnalisées */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Couleur principale</label>
          <div className="flex items-center gap-2">
            <input
              type="color"
              value={form.primary_color}
              onChange={(e) => setForm({...form, primary_color: e.target.value})}
              className="w-12 h-12 rounded-lg cursor-pointer border border-gray-200"
            />
            <input
              type="text"
              value={form.primary_color}
              onChange={(e) => setForm({...form, primary_color: e.target.value})}
              className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Couleur secondaire</label>
          <div className="flex items-center gap-2">
            <input
              type="color"
              value={form.secondary_color}
              onChange={(e) => setForm({...form, secondary_color: e.target.value})}
              className="w-12 h-12 rounded-lg cursor-pointer border border-gray-200"
            />
            <input
              type="text"
              value={form.secondary_color}
              onChange={(e) => setForm({...form, secondary_color: e.target.value})}
              className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Couleur d'accent</label>
          <div className="flex items-center gap-2">
            <input
              type="color"
              value={form.accent_color}
              onChange={(e) => setForm({...form, accent_color: e.target.value})}
              className="w-12 h-12 rounded-lg cursor-pointer border border-gray-200"
            />
            <input
              type="text"
              value={form.accent_color}
              onChange={(e) => setForm({...form, accent_color: e.target.value})}
              className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm"
            />
          </div>
        </div>
      </div>

      {/* Aperçu */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Aperçu</label>
        <div 
          className="h-32 rounded-xl flex items-center justify-center"
          style={{ 
            background: `linear-gradient(135deg, ${form.primary_color} 0%, ${form.secondary_color} 100%)`
          }}
        >
          <span 
            className="px-4 py-2 rounded-lg font-medium"
            style={{ backgroundColor: form.accent_color, color: 'white' }}
          >
            Bouton d'action
          </span>
        </div>
      </div>

      <button
        onClick={handleSave}
        disabled={saving}
        className="px-6 py-2.5 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition disabled:opacity-50 flex items-center gap-2"
        data-testid="save-theme-btn"
      >
        {saving ? <RefreshCw className="animate-spin" size={16} /> : <Save size={16} />}
        Enregistrer
      </button>
    </div>
  );
}

function ContactTab({ store, onUpdate, saving }) {
  const [form, setForm] = useState({
    address: store.address || '',
    city: store.city || '',
    postal_code: store.postal_code || '',
    phone: store.phone || '',
    email: store.email || ''
  });

  const handleSave = () => {
    onUpdate(form);
  };

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="sm:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-1">Adresse</label>
          <input
            type="text"
            value={form.address}
            onChange={(e) => setForm({...form, address: e.target.value})}
            placeholder="15 rue de la Paix"
            className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            data-testid="edit-address-input"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Code postal</label>
          <input
            type="text"
            value={form.postal_code}
            onChange={(e) => setForm({...form, postal_code: e.target.value})}
            placeholder="75001"
            className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            data-testid="edit-postal-input"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Ville</label>
          <input
            type="text"
            value={form.city}
            onChange={(e) => setForm({...form, city: e.target.value})}
            placeholder="Paris"
            className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            data-testid="edit-city-input"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Téléphone</label>
          <input
            type="tel"
            value={form.phone}
            onChange={(e) => setForm({...form, phone: e.target.value})}
            placeholder="06 12 34 56 78"
            className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            data-testid="edit-phone-input"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
          <input
            type="email"
            value={form.email}
            onChange={(e) => setForm({...form, email: e.target.value})}
            placeholder="contact@monentreprise.fr"
            className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            data-testid="edit-email-input"
          />
        </div>
      </div>

      <button
        onClick={handleSave}
        disabled={saving}
        className="px-6 py-2.5 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition disabled:opacity-50 flex items-center gap-2"
        data-testid="save-contact-btn"
      >
        {saving ? <RefreshCw className="animate-spin" size={16} /> : <Save size={16} />}
        Enregistrer
      </button>
    </div>
  );
}

function SocialTab({ store, onUpdate, saving }) {
  const links = store.social_links || {};
  const [form, setForm] = useState({
    facebook: links.facebook || '',
    instagram: links.instagram || '',
    linkedin: links.linkedin || '',
    twitter: links.twitter || '',
    youtube: links.youtube || '',
    tiktok: links.tiktok || '',
    website: links.website || ''
  });

  const handleSave = () => {
    onUpdate({ social_links: form });
  };

  const socialNetworks = [
    { key: 'facebook', label: 'Facebook', icon: Facebook, placeholder: 'https://facebook.com/...' },
    { key: 'instagram', label: 'Instagram', icon: Instagram, placeholder: 'https://instagram.com/...' },
    { key: 'linkedin', label: 'LinkedIn', icon: Linkedin, placeholder: 'https://linkedin.com/in/...' },
    { key: 'twitter', label: 'Twitter / X', icon: Twitter, placeholder: 'https://twitter.com/...' },
    { key: 'youtube', label: 'YouTube', icon: Youtube, placeholder: 'https://youtube.com/...' },
    { key: 'website', label: 'Site web', icon: Globe, placeholder: 'https://monsite.fr' },
  ];

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-500 mb-4">
        Ajoute tes liens réseaux sociaux pour que tes clients puissent te suivre.
      </p>

      {socialNetworks.map(({ key, label, icon: Icon, placeholder }) => (
        <div key={key} className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center">
            <Icon size={18} className="text-gray-500" />
          </div>
          <div className="flex-1">
            <input
              type="url"
              value={form[key]}
              onChange={(e) => setForm({...form, [key]: e.target.value})}
              placeholder={placeholder}
              className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              data-testid={`edit-social-${key}`}
            />
          </div>
        </div>
      ))}

      <button
        onClick={handleSave}
        disabled={saving}
        className="px-6 py-2.5 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition disabled:opacity-50 flex items-center gap-2 mt-6"
        data-testid="save-social-btn"
      >
        {saving ? <RefreshCw className="animate-spin" size={16} /> : <Save size={16} />}
        Enregistrer
      </button>
    </div>
  );
}
